/**
 * Messages Page Component
 * Main page for viewing and sending messages
 * Opens in new tab - standalone page without Layout wrapper
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import ConversationList from './messaging/ConversationList';
import MessageThread from './messaging/MessageThread';
import { Conversation } from '@/types/messaging';
import { messagingService } from '@/firebase/messagingService';
import { initBrowserTabBadge, updateBrowserTabBadge } from '@/utils/messageNotifications';
import { setupForegroundMessageListener } from '@/services/fcmService';
import { playNotificationSound, showMessageNotification } from '@/utils/messageNotifications';

const MessagesPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { currentOrganization, organizations } = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  
  // Check if conversation ID is in URL
  const conversationIdFromUrl = searchParams.get('conversation');
  const hasAnyOrganization = (currentOrganization?.id != null) || (organizations?.length > 0);

  useEffect(() => {
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      window.location.href = '/login';
    }
  }, [isAuthenticated]);

  // Initialize browser tab badge
  useEffect(() => {
    initBrowserTabBadge();
  }, []);

  // Set up FCM foreground message listener for push notifications
  useEffect(() => {
    if (!isAuthenticated) return;

    setupForegroundMessageListener((payload) => {
      console.log('📬 FCM foreground message received:', payload);
      
      // Play sound
      playNotificationSound();
      
      // Update browser tab badge
      updateBrowserTabBadge(1);
      
      // Show notification
      const notification = payload.notification;
      if (notification) {
        showMessageNotification(
          notification.title || 'New Message',
          notification.body || '',
          undefined
        );
      }
    });

    return () => {
      // Cleanup if needed (FCM listeners are typically managed by Firebase)
    };
  }, [isAuthenticated]);

  // Load conversation from URL parameter
  useEffect(() => {
    const loadConversationFromUrl = async () => {
      if (conversationIdFromUrl && user?.id) {
        try {
          const result = await messagingService.getConversation(conversationIdFromUrl);
          if (result.success && result.data) {
            setSelectedConversation(result.data);
            // Remove conversation param from URL after loading
            searchParams.delete('conversation');
            setSearchParams(searchParams, { replace: true });
          }
        } catch (error) {
          console.error('Error loading conversation from URL:', error);
        }
      }
    };

    loadConversationFromUrl();
  }, [conversationIdFromUrl, user?.id, searchParams, setSearchParams]);

  if (!isAuthenticated || !user) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">Please log in to view messages.</p>
        </div>
      </div>
    );
  }

  if (!hasAnyOrganization) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">Please select or create an organization to view messages.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Left Sidebar - Conversation List */}
      <div className="w-1/3 min-w-[360px] border-r border-gray-300 bg-white flex flex-col">
        <ConversationList
          onSelectConversation={setSelectedConversation}
          selectedConversationId={selectedConversation?.id}
        />
      </div>

      {/* Right Side - Message Thread */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedConversation ? (
          <MessageThread
            conversation={selectedConversation}
            onBack={() => setSelectedConversation(null)}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center" style={{ 
            backgroundColor: '#efeae2',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='whatsapp' x='0' y='0' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='25' cy='25' r='1' fill='%23d4d4d4' opacity='0.3'/%3E%3Ccircle cx='75' cy='75' r='1' fill='%23d4d4d4' opacity='0.3'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23whatsapp)'/%3E%3C/svg%3E")`
          }}>
            <div className="text-center">
              <div className="mb-4">
                <svg width="250" height="250" viewBox="0 0 250 250" className="mx-auto opacity-20">
                  <path fill="#54656f" d="M125 0C55.964 0 0 55.964 0 125s55.964 125 125 125 125-55.964 125-125S194.036 0 125 0zm0 230c-57.897 0-105-47.103-105-105S67.103 20 125 20s105 47.103 105 105-47.103 105-105 105z"/>
                  <path fill="#54656f" d="M125 55c-38.598 0-70 31.402-70 70s31.402 70 70 70 70-31.402 70-70-31.402-70-70-70zm0 120c-27.57 0-50-22.43-50-50s22.43-50 50-50 50 22.43 50 50-22.43 50-50 50z"/>
                </svg>
              </div>
              <p className="text-gray-500 text-lg">Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;

